import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ApiError,
  Button,
  FirstRunGuide,
  buttonVariants,
  useFirstRunGuide,
  type GuideStepDef,
} from '@dataroom/shared';
import type { Room, TenantPlan } from '@dataroom/shared';
import { useDdPhases, useRoomDocuments, useRoomSellers, useRoomsDashboard } from '@/api/hooks';
import {
  closingVaultLocked,
  downloadFromApi,
  SAMPLE_CHECKLIST_FILENAME,
  SAMPLE_CHECKLIST_XLSX_PATH,
} from '@/lib/checklist';
import { requestGuideTarget, type GuideTarget } from '@/lib/guide-target';
import {
  checklistImportVideoUrl,
  NEW_ROOM_NAME_INPUT_ID,
  SUBSCRIBER_GUIDE_KEY,
  subscriberFacts,
  subscriberRoom,
  trialFacts,
  trialRoom,
  TRIAL_GUIDE_KEY,
  type RoomFacts,
  type SubscriberFacts,
  type TrialFacts,
} from '@/lib/guides';

/** A guide step's link into the feature. Opening it does not tick the step: a step
 * ticks from the room's own data, or from the person actually doing the thing. */
function GoLink({ to, label, target }: { to: string; label: string; target?: GuideTarget }) {
  const { pathname } = useLocation();
  // A button for the page you are already on must still DO something: ask the Data
  // room page to show its part (the drop zone, the list, Ask, search), else go to the top.
  const onClick = () => {
    if (target) requestGuideTarget(target);
    else if (pathname === to) window.scrollTo?.({ top: 0, behavior: 'smooth' });
  };
  return (
    <Link to={to} onClick={onClick} className={buttonVariants({ size: 'sm', variant: 'outline' })}>
      {label}
    </Link>
  );
}

function focusNewRoomName() {
  const input = document.getElementById(NEW_ROOM_NAME_INPUT_ID);
  input?.scrollIntoView?.({ block: 'center' });
  input?.focus();
}

/** The trial's optional extra: bring your own checklist, with the 1-minute video. */
function BringYourOwnChecklist() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function download() {
    setError(null);
    setDownloading(true);
    try {
      await downloadFromApi(SAMPLE_CHECKLIST_XLSX_PATH, SAMPLE_CHECKLIST_FILENAME);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Download failed.');
    } finally {
      setDownloading(false);
    }
  }
  return (
    <div className="space-y-1">
      <p>
        <span className="font-medium">Optional: bring your own checklist.</span>{' '}
        <span className="text-muted-foreground">
          Fill in a spreadsheet and your room&apos;s folders and requests are built from it.
        </span>
      </p>
      <p className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <button
          type="button"
          className="font-medium text-primary hover:underline disabled:opacity-50"
          onClick={() => void download()}
          disabled={downloading}
        >
          {downloading ? 'Preparing…' : 'Download the sample layout'}
        </button>
        <a
          href={checklistImportVideoUrl()}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary hover:underline"
        >
          Watch how (1 min)
        </a>
      </p>
      {error ? (
        <p className="text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The trial's seven-to-eight things to try, in order (exported for the tests). */
export function trialSteps(room: Room | undefined, facts: TrialFacts): GuideStepDef[] {
  const base = room ? `/rooms/${room.id}` : null;
  const go = (suffix: string, label: string, target?: GuideTarget) =>
    base ? () => <GoLink to={`${base}${suffix}`} label={label} target={target} /> : undefined;
  // Step and sub-step ids that match a noted activity tick when the person does it.
  return [
    {
      id: 'room',
      title: 'Create a room for one live deal',
      // A new room has no checklist until one is set up (owner 2026-11-03): the
      // standard one is the dialog's preselected default, or the person uploads a
      // spreadsheet. The step stays open until the room has one.
      description:
        'Name it after the deal. Then set up its checklist: the standard one is ready, or upload your own spreadsheet.',
      done: facts.hasRoom && facts.hasChecklist,
      action: base
        ? go('/checklist', 'Set up the checklist')
        : () => (
            <Button size="sm" variant="outline" onClick={focusNewRoomName}>
              Name your room
            </Button>
          ),
    },
    {
      id: 'drop',
      title: 'Drop in a batch',
      description:
        'Whatever you have, scans included. Add a whole folder if you like; every file is read and filed for you.',
      done: facts.hasDocuments,
      action: go('', 'Open the room', 'upload'),
    },
    {
      id: 'processing',
      title: 'Watch it work',
      description:
        'The Processing page shows a live time estimate for the whole batch, stage by stage.',
      action: go('/processing', 'Open Processing'),
    },
    {
      id: 'filed',
      title: 'See it filed',
      description: 'Documents land in the right folders and answer the checklist on their own.',
      parts: [
        {
          id: 'checklist',
          label: 'Open the checklist and watch items tick themselves off',
          done: facts.filed,
          action: go('/checklist', 'Open the checklist'),
        },
        {
          id: 'manage-document',
          label:
            'Move a document or fix its date: in the document list, choose “Change folder or date” on its row',
          done: facts.personEdited,
          action: go('', 'Change a document', 'manage'),
        },
      ],
    },
    {
      id: 'redaction',
      title: 'See redaction',
      description:
        'Open a bank statement or tax return: account numbers and SSNs are already blacked out.',
      done: facts.redacted,
      action: go('', 'Open the Data room', 'documents'),
    },
    {
      id: 'ask',
      title: 'Ask the Data Room',
      description: 'Try “show me the bank statements”.',
      action: go('', 'Ask the Data Room', 'ask'),
    },
    {
      id: 'search',
      title: 'Find anything',
      description: 'Search by any word in a document’s name, folder or date.',
      action: go('', 'Search the Data room', 'search'),
    },
    {
      id: 'invite',
      title: 'Invite the seller',
      description:
        'They land on their own dashboard. Then set the phase dates and release Phase 1.',
      parts: [
        {
          id: 'seller',
          label: 'Invite the seller',
          done: facts.sellerInvited,
          action: go('/access', 'Open Access'),
        },
        {
          id: 'release',
          label: 'Set phase dates and release Phase 1',
          done: facts.phaseReleased,
          action: go('/checklist', 'Open the checklist'),
        },
      ],
    },
  ];
}

/** The new subscriber's four steps (exported for the tests). */
export function subscriberSteps(
  room: Room | undefined,
  facts: SubscriberFacts,
  closingVault = true,
): GuideStepDef[] {
  const base = room ? `/rooms/${room.id}` : null;
  const go = (suffix: string, label: string) =>
    base ? () => <GoLink to={`${base}${suffix}`} label={label} /> : undefined;
  return [
    {
      id: 'room',
      title: closingVault
        ? 'Create a room with the ClosingVault checklist'
        : 'Create a room with the standard checklist',
      description: closingVault
        ? 'Name the room, then keep “ClosingVault checklist” when it asks where the checklist comes from.'
        : 'Name the room, then keep “Standard checklist” when it asks where the checklist comes from.',
      done: facts.roomReady,
      action: base
        ? go('/checklist', 'Open the checklist')
        : () => (
            <Button size="sm" variant="outline" onClick={focusNewRoomName}>
              Name your room
            </Button>
          ),
    },
    {
      id: 'seller',
      title: 'Invite the seller and the owners',
      description:
        'Add each owner with their ownership percentage. The owners decide by majority of ownership.',
      done: facts.sellerInvited,
      action: go('/access', 'Open Access'),
    },
    {
      id: 'release',
      title: 'Set phase dates and release Phase 1',
      description: 'Sellers see a phase’s requests once it is released.',
      done: facts.phaseReleased,
      action: go('/checklist', 'Open the checklist'),
    },
    {
      id: 'buyers',
      title: 'Invite buyers with an access end date',
      description: 'Buyer access always ends on a date you choose, up to two months out.',
      done: facts.buyersInvited,
      action: go('/access', 'Open Access'),
    },
  ];
}

function TrialPanel({
  room,
  facts,
  compact,
}: {
  room: Room | undefined;
  facts: TrialFacts;
  compact: boolean;
}) {
  const guide = useFirstRunGuide(TRIAL_GUIDE_KEY);
  return (
    <FirstRunGuide
      guide={guide}
      title="See what ClosingVault does"
      ariaLabel="Free trial guide"
      intro="Eight things to try on one real deal. Each one opens the real feature, and the list ticks itself off as you go."
      steps={trialSteps(room, facts)}
      compact={compact}
      footer={<BringYourOwnChecklist />}
      resumeLabel="Show the getting-started guide"
    />
  );
}

/** The loaded facts of one room (the dashboard's checklist and buyers, the room's own lists). */
function useRoomFacts(room: Room | undefined): RoomFacts {
  const roomId = room?.id ?? '';
  const dashboard = useRoomsDashboard();
  const documents = useRoomDocuments(roomId, { enabled: room !== undefined });
  const sellers = useRoomSellers(roomId);
  const phases = useDdPhases(roomId);
  const entry = dashboard.data?.find((e) => e.roomId === roomId);
  return {
    checklist: entry?.checklist,
    buyers: entry?.buyers,
    documents: documents.data,
    sellers: sellers.data?.sellers,
    phases: phases.data,
  };
}

// The room-reading hooks only mount once there is a room (a hook cannot be skipped
// for an empty id), so each panel has a with-room wrapper.
function TrialGuideForRoom({ room, compact }: { room: Room; compact: boolean }) {
  const facts = useRoomFacts(room);
  return <TrialPanel room={room} facts={trialFacts(true, facts)} compact={compact} />;
}

/** The free trial's first-run guide, "See what ClosingVault does": on the Rooms page and in the trial's room. */
export function TrialGuide({
  rooms,
  compact = false,
}: {
  rooms: readonly Room[];
  /** Inside the room: only the step you are on is opened out. */
  compact?: boolean;
}) {
  const room = trialRoom(rooms);
  return room ? (
    <TrialGuideForRoom room={room} compact={compact} />
  ) : (
    <TrialPanel room={undefined} facts={trialFacts(false, {})} compact={compact} />
  );
}

function SubscriberPanel({
  room,
  facts,
  professional,
  closingVault,
}: {
  room: Room | undefined;
  facts: SubscriberFacts;
  professional: boolean;
  closingVault: boolean;
}) {
  const guide = useFirstRunGuide(SUBSCRIBER_GUIDE_KEY);
  const intro: ReactNode = (
    <>
      Four steps from a new room to a buyer looking at it.
      {professional ? ' Your uploads get priority processing.' : null}
    </>
  );
  return (
    <FirstRunGuide
      guide={guide}
      title="Get your first deal running"
      ariaLabel="New subscriber guide"
      intro={intro}
      steps={subscriberSteps(room, facts, closingVault)}
      resumeLabel="Show the first-deal guide"
    />
  );
}

function SubscriberGuideForRoom({
  room,
  professional,
  closingVault,
}: {
  room: Room;
  professional: boolean;
  closingVault: boolean;
}) {
  const facts = useRoomFacts(room);
  return (
    <SubscriberPanel
      room={room}
      facts={subscriberFacts(room, facts)}
      professional={professional}
      closingVault={closingVault}
    />
  );
}

/** A new paid subscriber's guide, "Get your first deal running", on the Rooms page. */
export function SubscriberGuide({ rooms, plan }: { rooms: readonly Room[]; plan: TenantPlan }) {
  const room = subscriberRoom(rooms, plan);
  const professional = plan.tier === 'professional';
  const closingVault = !closingVaultLocked(plan);
  return room ? (
    <SubscriberGuideForRoom room={room} professional={professional} closingVault={closingVault} />
  ) : (
    <SubscriberPanel
      room={undefined}
      facts={subscriberFacts(undefined, {})}
      professional={professional}
      closingVault={closingVault}
    />
  );
}
